// Task 1 · "Verbreek de verbinding" (opdrachten-browsertechniek.md #4).
// The hidden message is already on the device; it only shows while the device is offline.
// Detection: navigator.onLine plus a ping to our own server (airplane mode sometimes leaves
// wifi on, and navigator.onLine alone is not reliable).

import { api } from '../api.js';
import { sfx, vibrate } from '../audio.js';
import { backstage } from '../backstage.js';
import { openSheet } from '../sheet.js';
import { el, sleep } from '../util.js';

export interface OfflineResult { method: 'offline' | 'hold' }

export async function runOffline(): Promise<OfflineResult> {
  const sheet = openSheet({
    label: 'Opdracht 1 van 3',
    title: 'Verbreek de verbinding',
    quote: '“Ze lezen mee. Zet je wifi en mobiele data uit, of zet je toestel in vliegtuigmodus. Wat Ellen verstopte, kan je alleen zien als niemand meer meekijkt.”',
  });

  const wifi = el('span', {}, 'aan');
  const ping = el('span', {}, 'bereikbaar');
  const status = el('div.netstatus', {}, el('div', {}, el('b', {}, 'netwerk'), wifi), el('div', {}, el('b', {}, 'kanaal'), ping));
  const watchers = el('p.watchers', {}, '2 apparaten lezen mee');
  const note = el('p.sheet__note', {}, 'Buiten het verhaal: het bericht staat al op je toestel. Er wordt niets verstuurd of opgenomen. Zet daarna gewoon je verbinding weer aan.');
  const alt = el('button.btn.btn--ghost.btn--small', { type: 'button' }, 'Kan nu niet');
  const actions = el('div.sheet__actions', {}, alt);
  sheet.body.append(status, watchers, note, actions);

  const offline = await new Promise<OfflineResult>((resolve) => {
    let failures = 0;
    let stopped = false;
    const stop = (r: OfflineResult) => { if (stopped) return; stopped = true; clearInterval(poll); removeEventListener('offline', onOffline); resolve(r); };
    const render = (netOn: boolean, reachable: boolean) => {
      wifi.textContent = netOn ? 'aan' : 'uit';
      wifi.classList.toggle('is-off', !netOn);
      ping.textContent = reachable ? 'bereikbaar' : 'onbereikbaar';
      ping.classList.toggle('is-off', !reachable);
    };
    const check = async () => {
      if (stopped) return;
      const netOn = navigator.onLine;
      const reachable = netOn ? await api.ping(1200) : false;
      failures = reachable ? 0 : failures + 1;
      render(netOn, reachable);
      if (!netOn || failures >= 2) stop({ method: 'offline' });
    };
    const onOffline = () => { render(false, false); stop({ method: 'offline' }); };
    addEventListener('offline', onOffline);
    const poll = window.setInterval(check, 1500);
    void check();

    // Alternative for whoever cannot or will not go offline (spelverloop.md §2.3)
    alt.addEventListener('click', () => {
      backstage.log('verhaal', 'Alternatief gekozen', 'Elke opdracht heeft een alternatief. Hier: duim op het scherm houden, “zodat niemand meekijkt”.');
      const ring = el('div.hold', { role: 'button', tabindex: 0 },
        el('span', {}, 'Hou je duim hier'),
        el('span', { html: '<svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="47" fill="none" stroke="#26262a" stroke-width="4"/><circle id="hold-arc" cx="50" cy="50" r="47" fill="none" stroke="#ff6a1a" stroke-width="4" stroke-linecap="round" stroke-dasharray="295.3" stroke-dashoffset="295.3"/></svg>' }));
      actions.replaceChildren(el('p.small', { style: 'text-align:center;margin:0' }, '“Dan zo. Leg je duim op het scherm en laat niet los. Zolang je duim erop ligt, ziet niemand iets.”'), ring);
      const arc = () => ring.querySelector<SVGCircleElement>('#hold-arc')!;
      let start = 0, raf = 0;
      const tick = () => {
        const p = Math.min(1, (performance.now() - start) / 3000);
        arc().style.strokeDashoffset = String(295.3 * (1 - p));
        if (p >= 1) { vibrate(30); stop({ method: 'hold' }); return; }
        raf = requestAnimationFrame(tick);
      };
      const down = (e: Event) => { e.preventDefault(); ring.classList.add('is-holding'); start = performance.now(); raf = requestAnimationFrame(tick); };
      const up = () => { ring.classList.remove('is-holding'); cancelAnimationFrame(raf); if (!stopped) arc().style.strokeDashoffset = '295.3'; };
      ring.addEventListener('pointerdown', down);
      ring.addEventListener('pointerup', up); ring.addEventListener('pointerleave', up); ring.addEventListener('pointercancel', up);
      ring.addEventListener('contextmenu', (e) => e.preventDefault());
    });
  });

  backstage.log('sensor', offline.method === 'offline' ? 'Opdracht 1: verbinding verbroken' : 'Opdracht 1: duim op het scherm',
    offline.method === 'offline'
      ? 'Vastgesteld met <code>navigator.onLine</code> en een ping naar onze eigen server. Het geheime bericht zat al in de app: zonder netwerk kan het toch verschijnen.'
      : 'Pointer Events: drie seconden ononderbroken aanraken.');

  // The reveal, in the dark
  sheet.setDark(true);
  sfx.success();
  watchers.textContent = '0 apparaten lezen mee';
  watchers.style.color = 'var(--ok)';
  const reveal = el('div.reveal', {},
    el('div.reveal__photo', {}, el('img', { src: '/assets/door.jpg', alt: 'Een groene deur in een bakstenen gevel, met een jaartal in de steen erboven' })),
    el('div.reveal__text', { html: `Als iemand dit vindt: kijk naar de deur. Onthoud wat erboven staat. Het is de sleutel tot de rest.<small>Ellen · verborgen bericht · 21 dagen geleden</small>` }),
  );
  const back = el('button.btn.btn--primary', { type: 'button', disabled: offline.method === 'offline' }, offline.method === 'offline' ? 'Zet je verbinding weer aan…' : 'Ik heb het gezien');
  sheet.body.replaceChildren(reveal, el('div.sheet__actions', {}, back));

  if (offline.method === 'offline') {
    // Wait until the device is back online before the story continues (it needs the server again)
    void (async () => {
      while (!(navigator.onLine && (await api.ping(1500)))) await sleep(1200);
      back.disabled = false;
      back.textContent = 'Ik heb het gezien';
      sheet.feedback('Verbinding hersteld.', 'good');
    })();
  }
  await new Promise<void>((r) => back.addEventListener('click', () => r(), { once: true }));
  await sheet.close();
  return offline;
}
